import { BUILDINGS } from "@zend/content";
import { POWER_DEFICIT_PRODUCTION_MULT } from "@zend/rules";
import { AssetIcon } from "../../assets/Icon.js";
import { pick, useT } from "../../i18n/index.js";
import { Timer, formatDuration } from "../../ui/Timer.js";
import { navigate } from "../../router.js";
import { Button } from "../../ui/Button.js";
import { baseApi } from "../api.js";
import { useBase } from "../state.js";
import { firstFreeSpot } from "./BuildMenu.js";
import { Sheet, CostRow, buildingName } from "./shared.js";
import { PayAction } from "./PayAction.js";
import { CollectionPanel, MoralePanel } from "../ColonyPanels.js";
import { ProgressionPanel } from "../ProgressionPanel.js";
import { Icon } from "../../ui/Icon.js";
import "../hq-panel.css";

/** Querschnitt gauge, drag list with one-tap fixes, power and the entry points to the other base panels (R11, R19). */
export function HqPanel() {
  const t = useT();
  const base = useBase((s) => s.base);
  const run = useBase((s) => s.run);
  const setPanel = useBase((s) => s.setPanel);
  const select = useBase((s) => s.select);
  const setMode = useBase((s) => s.setMode);
  const setGhost = useBase((s) => s.setGhost);
  if (!base) return null;
  const hq = base.buildings.find((b) => b.buildingId === "bld.command_nexus");
  const running = hq?.state === "upgrading";
  const nextLevel = running ? hq.targetLevel : base.enclave.hqLevel + 1;
  const unlocks = BUILDINGS.filter((b) => b.unlock.first === nextLevel);

  const build = (buildingId: string) => {
    const def = BUILDINGS.find((b) => b.id === buildingId);
    if (!def || def.footprint === "ring") return;
    const spot = firstFreeSpot(base, def.footprint.w, def.footprint.h);
    setMode({ kind: "place", buildingId, w: def.footprint.w, h: def.footprint.h, rot: 0 });
    setGhost(spot ? { ...spot, valid: true } : { x: base.grid.core.x, y: base.grid.core.y, valid: false });
    setPanel({ kind: "none" });
  };

  return (
    <Sheet title={t("base.hq.title", { n: base.enclave.hqLevel })} onClose={() => setPanel({ kind: "none" })} testId="hq-panel">
      <div className="hq-next section">
        <AssetIcon id="bld.command_nexus" buildingLevel={base.enclave.hqLevel} size={176} />
        <div>
          <p className="hint">{pick({ de: "Dein nächster Ausbau", en: "Your next upgrade" })}</p>
          <h2>{base.gate.maxed ? t("base.gate.maxed") : `HQ ${nextLevel}`}</h2>
          <span className={`badge ${base.gate.ok ? "ok" : "warn"}`} data-testid="gate-badge">
            {running ? pick({ de: "Im Bau", en: "Under construction" }) : base.gate.maxed ? t("base.gate.maxed") : base.gate.ok ? t("base.gate.ok") : t("base.gate.blocked", { n: base.gate.drags.length })}
          </span>
          <p className="mono"><Icon name="clock" /> {running && hq.finishesAt ? <Timer finishesAt={hq.finishesAt} /> : hq?.next ? formatDuration(hq.next.timeMs) : "—"}</p>
        </div>
        {hq && !running && hq.next ? <div className="hq-upgrade-action">
          <CostRow cost={hq.next.cost} />
          <PayAction cost={hq.next.cost} reasons={hq.canUpgrade.reasons} showReasons={base.gate.ok} onStart={(cover) => run(() => baseApi.upgrade(hq.id, cover), t("base.toast.upgradeStarted"))} label={<><Icon name="arrowUp" size={17} /> {t("base.hq.upgrade", { n: hq.level + 1 })}</>} variant="gold" testId="hq-upgrade" />
        </div> : null}
        {running ? <Button className="hq-upgrade-action" onClick={() => select(hq.id)} data-testid="hq-view-build">{pick({ de: "Bauauftrag ansehen", en: "View construction" })}</Button> : null}
      </div>

      {unlocks.length && !base.gate.maxed ? <div className="section">
        <h3>{pick({ de: "Damit schaltest du frei", en: "This unlocks" })}</h3>
        <div className="hq-upgrade-unlocks">{unlocks.map((b) => <div key={b.id}><AssetIcon id={b.id} buildingLevel={1} size={72} /><span>{buildingName(b.id)}</span></div>)}</div>
      </div> : null}

      {base.gate.drags.length > 0 ? (
        <div className="section" data-testid="gate-drags">
          <h3>{pick({ de: "Das fehlt noch", en: "Still required" })}</h3>
          {base.gate.drags.map((d, i) => {
            const row = base.buildings.filter((b) => b.buildingId === d.id).sort((a, b) => a.level - b.level)[0];
            return (
              <div key={`${d.id}-${i}`} className="row list-row hq-requirement">
                <AssetIcon id={d.id} buildingLevel={row?.level ?? 1} size={56} />
                <span className="grow">
                  <strong>{buildingName(d.id)}</strong>
                  <div className="hint">
                    Lv {d.level} <Icon name="arrowRight" size={12} /> {base.gate.required}
                  </div>
                </span>
                {row ? (
                  <Button onClick={() => select(row.id)} data-testid={`drag-fix-${d.id}`}>
                    {t("base.show")}
                  </Button>
                ) : (
                  <Button variant="primary" onClick={() => build(d.id)} data-testid={`drag-build-${d.id}`}>
                    {t("base.build.action")}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      <div className="hq-shortcuts">
        <Button onClick={() => setPanel({ kind: "queue" })} data-testid="open-queues">{t("base.queue.title")}</Button>
        <Button onClick={() => { setPanel({ kind: "none" }); navigate("/app/inventory"); }}>{pick({ de: "Mein Lager", en: "My inventory" })}</Button>
      </div>
      <details className="hq-details"><summary>{pick({ de: "Ausbauvoraussetzungen", en: "Upgrade requirements" })}</summary>
        <p>{t("base.gate.summary", { avg: base.gate.average.toFixed(2), req: base.gate.required })}</p>
        <p className="hint">{t("base.gate.hint")}</p>
      </details>
      <details className="hq-details"><summary>{pick({ de: "Versorgung & Moral", en: "Supply & morale" })}</summary>
      <div className="section">
        <h3>{t("base.power.title")}</h3>
        <div className="row" style={{ gap: 14, flexWrap: "wrap" }}>
          <span className="mono">
            {base.power.produced} / {base.power.consumed}
          </span>
          <span className={`badge ${base.power.deficit ? "warn" : "ok"}`}>{base.power.deficit ? t("base.power.deficit", { pct: POWER_DEFICIT_PRODUCTION_MULT * 100 }) : t("base.power.ok")}</span>
          <span className="mono dim">{t("base.power.score", { n: base.enclave.power })}</span>
        </div>
        <div className="row" style={{ gap: 14, flexWrap: "wrap", marginTop: 6 }} data-testid="production-rates">
          {Object.entries(base.production).map(([k, v]) => (
            <span key={k} className="mono" style={{ fontSize: 12 }}>
              <span style={{ color: `var(--res-${k})` }}>{k}</span> +{v}/h
            </span>
          ))}
          <span className="mono hint">{t("base.capacity", { n: base.capacity })}</span>
        </div>
      </div>

      <ProgressionPanel />
      <CollectionPanel />
      <MoralePanel />
      </details>
      <details className="hq-details"><summary>{pick({ de: "Basis verwalten", en: "Manage base" })}</summary>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <Button onClick={() => setPanel({ kind: "zones" })} data-testid="open-zones">
          {t("base.zones.title")}
        </Button>
        <Button onClick={() => setPanel({ kind: "protocols" })} data-testid="open-protocols">
          {t("base.protocols.title")}
        </Button>
        <Button onClick={() => setPanel({ kind: "research" })} data-testid="open-research">
          {t("research.title")}
        </Button>
        <Button onClick={() => setPanel({ kind: "layouts" })} data-testid="open-layouts">
          {t("base.layouts.title")}
        </Button>
      </div>

      <div className="section">
        <h3>{t("base.all")}</h3>
        {[...base.buildings]
          .sort((a, b) => a.level - b.level || a.buildingId.localeCompare(b.buildingId))
          .map((b) => (
            <button key={b.id} type="button" className="row list-row plain" onClick={() => select(b.id)} data-testid={`list-${b.buildingId}`}>
              <AssetIcon id={b.buildingId} size={32} />
              <span className="grow ellipsis">{buildingName(b.buildingId)}</span>
              {b.state !== "idle" ? <span className="badge"><Icon name="clock" size={14} /></span> : null}
              <span className="badge gold">Lv {b.level}</span>
            </button>
          ))}
      </div>
      </details>
    </Sheet>
  );
}
